import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import EquipmentCard from './EquipmentCard';
import { IGasType } from './EquipmentDetail';

interface IEquipmentMetric {
    label: string;
    value: string;
    unit: number;
    gasType?: IGasType;
}

interface IEquipmentDevice {
    id: string;
    name: string;
    serialNumber?: string;
    category: string;
    imageUrl: string;
    building?: string;
    floor?: string;
    room?: string;
    metric?: IEquipmentMetric;
    alarmCount?: number;
    hasAlert: boolean;
}

export interface IEquipmentCategory {
    id: string;
    name: string;
    devices: IEquipmentDevice[];
    isCollapsed: boolean;
}

interface EquipmentCategoryGroupProps {
    category: IEquipmentCategory;
    onToggleCollapse: (categoryId: string) => void;
    onCardClick: (deviceId: string) => void;
}

const EquipmentCategoryGroup: React.FC<EquipmentCategoryGroupProps> = ({
    category,
    onToggleCollapse,
    onCardClick
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [windowWidth, setWindowWidth] = useState(
        typeof window !== 'undefined' ? window.innerWidth : 1920
    );

    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Determine if category should be shown as a stack (more than 1 device)
    const shouldStack = category.devices.length > 1;

    const toggleStackExpansion = (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsExpanded(!isExpanded);
    };

    // Calculate total alarms for category
    const totalAlarmCount = category.devices.reduce(
        (sum, device) => sum + (device.alarmCount || 0),
        0
    );

    const hasAlert = category.devices.some(device => device.hasAlert);

    const itemsCount = isExpanded ? category.devices.length : 0;

    // Calculate max columns based on viewport width
    const getMaxColumns = () => {
        if (windowWidth <= 768) return 1;
        if (windowWidth <= 1024) return 2;
        if (windowWidth <= 1200) return 3;
        return 4;
    };

    // Calculate grid column span: constrained by viewport and item count
    const gridColumnSpan = isExpanded
        ? Math.min(itemsCount, getMaxColumns())
        : 1;

    return (
        <div
            className="equipment-category-group"
            data-items-count={itemsCount}
            style={{ gridColumn: `span ${gridColumnSpan}` }}
        >
            {!category.isCollapsed && (
                <div className="category-cards-container">
                    {shouldStack && !isExpanded ? (
                        // Show stacked card for entire category
                        <div
                            className="stacked-card-wrapper"
                            onClick={toggleStackExpansion}
                        >
                            <div className="stack-layers">
                                <div className="stack-layer layer-3" />
                                <div className="stack-layer layer-2" />
                                <EquipmentCard
                                    device={{
                                        ...category.devices[0],
                                        alarmCount: totalAlarmCount,
                                        hasAlert
                                    }}
                                    onClick={() => {
                                        console.log('clicked');
                                    }}
                                    isStacked={true}
                                    stackCount={category.devices.length}
                                    category={category}
                                />
                            </div>
                        </div>
                    ) : shouldStack && isExpanded ? (
                        // Show expanded group
                        <div
                            data-items-count={itemsCount}
                            className="expanded-group-container"
                            style={{ gridColumn: `span ${gridColumnSpan}` }}
                        >
                            <div className="expanded-group-header">
                                <p className="expanded-group-title">
                                    {category.name}{' '}
                                    <span className="expanded-group-item-count">
                                        {category.devices.length}
                                    </span>
                                </p>
                                <button
                                    className="collapse-group-button"
                                    onClick={toggleStackExpansion}
                                    aria-label="Collapse group"
                                >
                                    <FontAwesomeIcon
                                        icon={['far', 'chevron-double-left']}
                                        className="collapse-icon"
                                    />
                                    <span className="sr-only">Collapse</span>
                                </button>
                            </div>
                            <div
                                className="expanded-group-cards"
                                style={
                                    {
                                        '--columns': gridColumnSpan
                                    } as React.CSSProperties
                                }
                            >
                                {category.devices.map(device => (
                                    <EquipmentCard
                                        key={device.id}
                                        device={device}
                                        onClick={onCardClick}
                                        category={category}
                                    />
                                ))}
                            </div>
                        </div>
                    ) : (
                        // Show single card for categories with only 1 device
                        category.devices.map(device => (
                            <EquipmentCard
                                key={device.id}
                                device={device}
                                onClick={onCardClick}
                                category={category}
                            />
                        ))
                    )}
                </div>
            )}
        </div>
    );
};

export default EquipmentCategoryGroup;
